/* The console frame: sidebar (collapsible on desktop, a drawer on phones),
   the top bar with search and the account menu, and the scrolling content
   area. The frame is fixed to the viewport and scrolls its own content,
   because the app locks <body> for its native-feeling screens. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronsUpDown, ExternalLink, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Search,
} from 'lucide-react';
import { Command as CommandPrimitive } from 'cmdk';
import { cn } from '@/lib/utils';
import { useUser } from '@/contexts/UserContext';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import logo from '@/res/betterhusband_black.png';
import { NAV, SECTIONS, sectionById } from './sections';
import { displayName, useTickets, useUsers } from './lib';
import { Avatar, Pill } from './ui';

const COLLAPSED_KEY = 'bh_admin_sidebar_collapsed';
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

function useOpenTicketCount() {
  const { data } = useTickets();
  return useMemo(() => (data || []).filter((t) => t.status === 'open').length, [data]);
}

function NavList({ active, onNavigate, collapsed, badges }) {
  return (
    <nav className="flex-1 overflow-y-auto px-2 py-3">
      {NAV.map((group, gi) => (
        <div key={group.group} className={cn(gi > 0 && 'mt-5')}>
          {!collapsed && (
            <p className="px-2.5 pb-1.5 text-[11px] font-medium text-muted-foreground/80">{group.group}</p>
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              const isActive = active === item.id;
              const badge = badges[item.id];
              const button = (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onNavigate(item.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex h-8 w-full items-center gap-2.5 rounded-md px-2.5 text-[13px] transition-colors',
                    collapsed && 'justify-center px-0',
                    isActive
                      ? 'bg-secondary font-medium text-foreground'
                      : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
                  )}
                >
                  <span className="relative">
                    <Icon className="h-4 w-4 shrink-0" />
                    {collapsed && badge > 0 && (
                      <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-card" />
                    )}
                  </span>
                  {!collapsed && <span className="flex-1 truncate text-left">{item.label}</span>}
                  {!collapsed && badge > 0 && (
                    <span className="tabular rounded-full bg-red-50 px-1.5 text-[11px] font-medium text-red-700">{badge}</span>
                  )}
                </button>
              );
              if (!collapsed) return button;
              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>{button}</TooltipTrigger>
                  <TooltipContent side="right">{item.label}{badge > 0 ? ` · ${badge}` : ''}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function Brand({ collapsed }) {
  return (
    <div className={cn('flex h-14 shrink-0 items-center gap-2.5 border-b border-border px-4', collapsed && 'justify-center px-0')}>
      <img src={logo} alt="" className="h-6 w-6 shrink-0 rounded-md" />
      {!collapsed && (
        <div className="min-w-0 leading-tight">
          <p className="truncate text-[13px] font-semibold text-foreground">Better Husband</p>
          <p className="text-[11px] text-muted-foreground">Admin</p>
        </div>
      )}
    </div>
  );
}

function CommandPalette({ open, onOpenChange, onNavigate, onOpenCustomer }) {
  const [query, setQuery] = useState('');
  const { data: users } = useUsers();
  const q = query.trim().toLowerCase();

  const sections = SECTIONS.filter((s) => !q || s.label.toLowerCase().includes(q) || s.description.toLowerCase().includes(q));
  const customers = useMemo(() => {
    if (q.length < 2 || !users) return [];
    return users
      .filter((u) => (u.name || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.user_id || '').toLowerCase().startsWith(q))
      .slice(0, 8);
  }, [q, users]);

  const close = () => {
    onOpenChange(false);
    setQuery('');
  };

  const itemClass = 'flex cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-2 text-sm outline-none data-[selected=true]:bg-secondary';

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent
        className="top-[15%] max-w-lg translate-y-0 gap-0 overflow-hidden rounded-xl p-0 sm:rounded-xl [&>button]:hidden"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Search the admin console</DialogTitle>
        <CommandPrimitive shouldFilter={false} loop className="flex flex-col">
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <CommandPrimitive.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Go to a section or find a customer…"
              className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <CommandPrimitive.List className="max-h-[min(380px,60vh)] overflow-y-auto p-2">
            <CommandPrimitive.Empty className="py-8 text-center text-sm text-muted-foreground">
              No matches
            </CommandPrimitive.Empty>
            {sections.length > 0 && (
              <CommandPrimitive.Group heading="Go to" className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
                {sections.map((s) => {
                  const Icon = s.icon;
                  return (
                    <CommandPrimitive.Item key={s.id} value={`section:${s.id}`} onSelect={() => { onNavigate(s.id); close(); }} className={itemClass}>
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1">{s.label}</span>
                      <span className="hidden truncate text-xs text-muted-foreground sm:block">{s.description}</span>
                    </CommandPrimitive.Item>
                  );
                })}
              </CommandPrimitive.Group>
            )}
            {customers.length > 0 && (
              <CommandPrimitive.Group heading="Customers" className="mt-1 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
                {customers.map((u) => (
                  <CommandPrimitive.Item key={u.user_id} value={`user:${u.user_id}`} onSelect={() => { onOpenCustomer(u.user_id); close(); }} className={itemClass}>
                    <Avatar user={u} size={24} />
                    <span className="min-w-0 flex-1 truncate">{displayName(u)}</span>
                    <span className="truncate text-xs text-muted-foreground">{u.email}</span>
                  </CommandPrimitive.Item>
                ))}
              </CommandPrimitive.Group>
            )}
            {q.length < 2 && (
              <p className="px-2.5 pb-1 pt-2 text-xs text-muted-foreground">Type two or more letters to search customers by name, email or ID.</p>
            )}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}

function AccountMenu() {
  const { user, removeToken } = useUser();
  const navigate = useNavigate();
  const signOut = async () => {
    try { await removeToken(); } finally { navigate('/login', { replace: true }); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-9 items-center gap-2 rounded-md px-1.5 text-left transition-colors hover:bg-secondary sm:pr-2"
        >
          <Avatar user={user} size={26} />
          <span className="hidden max-w-[160px] truncate text-[13px] font-medium text-foreground md:block">{displayName(user)}</span>
          <ChevronsUpDown className="hidden h-3.5 w-3.5 text-muted-foreground md:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-sm font-medium">{displayName(user)}</p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/dashboard')}>
          <ExternalLink className="h-4 w-4" /> Open the app
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={signOut} className="text-red-600 focus:text-red-600">
          <LogOut className="h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AdminShell({ section, onNavigate, onOpenCustomer, children }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const openTickets = useOpenTicketCount();
  const current = sectionById(section);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1'); } catch { /* ignore */ }
      return !c;
    });
  };

  const go = useCallback((id) => {
    setMobileNavOpen(false);
    onNavigate(id);
  }, [onNavigate]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const badges = { support: openTickets };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="fixed inset-0 flex bg-background text-foreground">
        {/* Desktop sidebar */}
        <aside
          className={cn(
            'hidden shrink-0 flex-col border-r border-border bg-card transition-[width] duration-200 lg:flex',
            collapsed ? 'w-[60px]' : 'w-60',
          )}
        >
          <Brand collapsed={collapsed} />
          <NavList active={section} onNavigate={go} collapsed={collapsed} badges={badges} />
          <div className="border-t border-border p-2">
            <button
              type="button"
              onClick={toggleCollapsed}
              className={cn(
                'flex h-8 w-full items-center gap-2.5 rounded-md px-2.5 text-[13px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
                collapsed && 'justify-center px-0',
              )}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
              {!collapsed && 'Collapse'}
            </button>
          </div>
        </aside>

        {/* Phone / tablet drawer */}
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="flex w-72 flex-col gap-0 p-0 sm:max-w-xs" aria-describedby={undefined}>
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <Brand collapsed={false} />
            <NavList active={section} onNavigate={go} collapsed={false} badges={badges} />
          </SheetContent>
        </Sheet>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-card/80 px-3 backdrop-blur sm:px-4">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground lg:hidden"
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex min-w-0 items-center gap-2 text-sm">
              <span className="hidden text-muted-foreground sm:inline">Admin</span>
              <span className="hidden text-muted-foreground/50 sm:inline">/</span>
              <span className="truncate font-medium text-foreground">{current?.label}</span>
              {section === 'support' && openTickets > 0 && <Pill tone="danger">{openTickets} open</Pill>}
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="flex h-9 items-center gap-2 rounded-md border border-border bg-card px-2.5 text-[13px] text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground"
              >
                <Search className="h-4 w-4" />
                <span className="hidden sm:inline">Search</span>
                <kbd className="ml-2 hidden rounded border border-border bg-secondary px-1.5 font-mono text-[10px] text-muted-foreground sm:inline">
                  {isMac ? '⌘K' : 'Ctrl K'}
                </kbd>
              </button>
              <AccountMenu />
            </div>
          </header>

          <main className="flex-1 overflow-y-auto overscroll-contain">
            <div className="mx-auto w-full max-w-[1400px] p-4 pb-16 sm:p-6 sm:pb-16">{children}</div>
          </main>
        </div>
      </div>

      <CommandPalette
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onNavigate={go}
        onOpenCustomer={onOpenCustomer}
      />
    </TooltipProvider>
  );
}
